import { useState, useEffect } from 'react';
import { fetchMessages, fetchMessageAttempts, fetchMessageDetails } from '../services/svixApi';

const getHttpStatusText = (statusCode) => {
  if (!statusCode) return 'N/A';
  const statusMap = {
    200: 'OK',
    201: 'Created',
    202: 'Accepted',
    204: 'No Content',
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    504: 'Gateway Timeout',
  };
  return statusMap[statusCode] || `Status ${statusCode}`;
};

function WebhookMessagesList() {
  const [messages, setMessages] = useState([]);
  const [iterator, setIterator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [selectedMessageDetails, setSelectedMessageDetails] = useState(null);
  const [messageAttempts, setMessageAttempts] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async (resetList = true) => {
    setLoading(true);
    try {
      const data = await fetchMessages(20, resetList ? null : iterator);
      
      if (resetList) {
        setMessages(data.messages);
      } else {
        setMessages([...messages, ...data.messages]);
      }
      
      setIterator(data.iterator);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const viewMessageDetailsAndAttempts = async (messageId) => {
    if (selectedMessageId === messageId) {
      setSelectedMessageId(null);
      setSelectedMessageDetails(null);
      setMessageAttempts([]);
      return;
    }

    try {
      setSelectedMessageId(messageId);
      setLoadingDetails(true);
      setSelectedMessageDetails(null);
      setMessageAttempts([]);

      const [details, attempts] = await Promise.all([
        fetchMessageDetails(messageId),
        fetchMessageAttempts(messageId)
      ]);

      setSelectedMessageDetails(details);
      setMessageAttempts(attempts);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load message details or attempts');
      setSelectedMessageDetails(null);
      setMessageAttempts([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch (e) {
      return 'Invalid Date';
    }
  };




  const getStatusBadge = (status) => {
    switch (status) {
      case 0:
        return { text: 'Success', className: 'bg-green-800 text-green-100' };
      case 1:
        return { text: 'Pending', className: 'bg-yellow-800 text-yellow-100' };
      case 2:
        return { text: 'Failed', className: 'bg-red-800 text-red-100' };
      case 3:
        return { text: 'Sending', className: 'bg-blue-800 text-blue-100' };
      default:
        return { text: 'Unknown', className: 'bg-gray-800 text-gray-100' };
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4 text-white">Webhook Messages</h1>

      {loading && messages.length === 0 && <p className="text-center text-gray-400">Loading...</p>}
      {error && <p className="text-center text-red-500">Error: {error}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          {messages.length > 0 && (
            <div className="overflow-x-auto bg-gray-800 shadow rounded-lg">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Event Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Timestamp</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-600">
                  {messages.map((message) => (
                    <tr
                      key={message.id}
                      className={`hover:bg-gray-700 ${selectedMessageId === message.id ? 'bg-gray-700' : ''}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100">{message.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{message.eventType}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{formatDate(message.timestamp)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        <button
                          onClick={() => viewMessageDetailsAndAttempts(message.id)}
                          className="text-blue-400 hover:text-blue-300"
                        >
                          {selectedMessageId === message.id ? 'Hide Details' : 'View Details'}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {messages.length === 0 && !loading && (
                    <tr>
                      <td colSpan={4} className="px-6 py-4 text-center text-gray-400">
                        No webhook messages found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {iterator && (
            <div className="mt-4 text-center">
              <button
                onClick={() => loadMessages(false)}
                disabled={loading}
                className="px-4 py-2 bg-gray-600 rounded disabled:opacity-50 hover:bg-gray-500 disabled:cursor-not-allowed"
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>

        <div className="bg-gray-800 shadow rounded-lg p-4 space-y-6">
          <h2 className="text-xl font-bold text-white">
            {selectedMessageId ? 'Message Details & Attempts' : 'Select a message to view details'}
          </h2>

          {loadingDetails && <p className="text-gray-400">Loading details...</p>}

          {selectedMessageId && !loadingDetails && selectedMessageDetails ? (
            <>
              <div>
                <h3 className="text-lg font-semibold mb-2 text-gray-200">Payload</h3>
                <pre className="bg-gray-900 p-3 rounded overflow-x-auto text-sm text-gray-300">
                  {JSON.stringify(selectedMessageDetails.payload, null, 2)}
                </pre>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-2 text-gray-200">Attempts</h3>
                {messageAttempts.length > 0 ? (
                  <div className="space-y-4">
                    {messageAttempts.map((attempt) => {
                      const badge = getStatusBadge(attempt.status);
                      return (
                        <div key={attempt.id} className="border border-gray-700 rounded p-3">
                          <div className="flex justify-between">
                            <span className="text-gray-300">Status:</span>
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${badge.className}`}>
                              {badge.text}
                            </span>
                          </div>
                          <div className="flex justify-between mt-2">
                            <span className="text-gray-300">Response:</span>
                            <span className="text-gray-300">
                              {attempt.responseStatusCode ? `${attempt.responseStatusCode} (${getHttpStatusText(attempt.responseStatusCode)})` : 'N/A'}
                            </span>
                          </div>
                          <div className="flex justify-between mt-2">
                            <span className="text-gray-300">Time:</span>
                            <span className="text-gray-300">{formatDate(attempt.timestamp)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-400">No attempt data available for this message.</p>
                )}
              </div>
            </>
          ) : (
            !loadingDetails && !error && <p className="text-gray-400">Select a message from the list to view its details and delivery attempts.</p>
          )}
          {!loadingDetails && error && selectedMessageId && (
             <p className="text-red-500">Error loading details: {error}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default WebhookMessagesList;